import { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, MenuProps, Space, Tooltip } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, ExportTemplateResponse, FieldType } from '@pmp/api';
import { Guard } from '@ohpen/react-authz';
import { ListProps } from '@pmp/types';

import { exportTemplatesColumns } from '../tableConfig';
import { getFilteredEntityTypeList } from '@pmp/utils';

const rowKey = 'id';

export const exportEntityTypes = [
  ActionEntityType.PRODUCTS,
  ActionEntityType.ERC_PROFILES,
  ActionEntityType.EXCHANGE_RATES,
  ActionEntityType.FEES,
  ActionEntityType.FEE_PROFILES,
];

export const ExportTemplatesList = memo(
  ({
    isFetching,
    valueLists = {},
    data,
    isCrudOpen,
    onView,
    onCreate,
    onClone,
    onEdit,
    onDelete,
  }: ListProps<ExportTemplateResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();

    const [buttonsLoading, setButtonsLoading] = useState<{ id: string; type: string }>({ id: '', type: '' });

    useEffect(() => {
      if (isCrudOpen || !buttonsLoading.id) return;
      setButtonsLoading({ id: '', type: '' });
    }, [buttonsLoading.id, isCrudOpen]);

    const handleOnView = useCallback(
      (record: ExportTemplateResponse) => {
        setButtonsLoading({ id: record.id, type: 'view' });
        onView(record);
      },
      [onView],
    );

    const handleOnEdit = useCallback(
      (record: ExportTemplateResponse) => {
        setButtonsLoading({ id: record.id, type: 'edit' });
        onEdit(record);
      },
      [onEdit],
    );

    const handleOnClone = useCallback(
      (record: ExportTemplateResponse) => {
        setButtonsLoading({ id: record.id, type: 'clone' });
        onClone?.(record);
      },
      [onClone],
    );

    const getRowActions: (record: ExportTemplateResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button
              aria-label="View export template"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleOnView(record)}
              loading={record.id === buttonsLoading.id && buttonsLoading.type === 'view'}
              disabled={!!buttonsLoading.id && (record.id !== buttonsLoading.id || buttonsLoading.type !== 'view')}
            />
          </Tooltip>,
          <Guard key="edit" permissions={['exportTemplates:update']} flags={['enableExports']}>
            <Tooltip key="edit" title={t('global.labels.edit')} placement="top">
              <Button
                aria-label="Edit export template"
                type="text"
                icon={<EditOutlined key="edit" />}
                onClick={() => handleOnEdit(record)}
                loading={record.id === buttonsLoading.id && buttonsLoading.type === 'edit'}
                disabled={
                  record.isDefaultTemplate || (!!buttonsLoading.id && (record.id !== buttonsLoading.id || buttonsLoading.type !== 'edit'))
                }
              />
            </Tooltip>
            <Tooltip key="clone" title={t('global.buttons.clone')} placement="top">
              <Button
                aria-label="Clone export template"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleOnClone(record)}
                loading={record.id === buttonsLoading.id && buttonsLoading.type === 'clone'}
                disabled={!!buttonsLoading.id && (record.id !== buttonsLoading.id || buttonsLoading.type !== 'clone')}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['exportTemplates:delete']} flags={['enableExports']}>
            <Tooltip key="delete" title={t('global.labels.delete')} placement="top">
              <Button
                aria-label="Delete export template"
                type="text"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={record.isDefaultTemplate || !!buttonsLoading.id}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [buttonsLoading.id, buttonsLoading.type, handleOnClone, handleOnEdit, handleOnView, onDelete, t],
    );

    const items: MenuProps['items'] = useMemo(
      () => exportEntityTypes.map(entityType => ({ key: entityType, label: t(`pmp.entities.${entityType}`) })),
      [t],
    );

    const tableProps: TableProps<ExportTemplateResponse> = useMemo(() => {
      const filteredEntitiesValueLists = {
        ...valueLists,
        [FieldType.ACTION_ENTITY_TYPE]: getFilteredEntityTypeList(valueLists, exportEntityTypes),
      };

      return {
        ref: tableRef,
        id: 'export-templates',
        dataSource: data ?? [],
        columns: exportTemplatesColumns,
        rowKey,
        pagination: true,
        manageColumns: false,
        scroll: { x: 'max-content', y: 'table-height' },
        valueLists: filteredEntitiesValueLists,
        loading: isFetching,
        getRowActions,
      };
    }, [valueLists, data, isFetching, getRowActions]);

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard permissions={['exportTemplates:create']} flags={['enableExports']}>
                <Table.CreateButton items={items} onClick={onCreate} loading={isCrudOpen} label={t('pmp.exports.createTemplate')} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
